import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { Subscription } from 'rxjs';
import jsQR from 'jsqr';
import { UserPortalService } from '../services/user-portal.service';

export type RefundStep = 'select-path' | 'scan-qr' | 'manual-info' | 'select-reason' | 'confirmed';
export type RefundPath = 'qr' | 'no-qr';

@Component({
  selector: 'app-refund',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './refund.component.html',
  styleUrls: ['./refund.component.scss']
})
export class RefundComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(Auth);
  private portalService = inject(UserPortalService);

  @ViewChild('scannerVideo') scannerVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('scannerCanvas') scannerCanvas?: ElementRef<HTMLCanvasElement>;

  currentStep: RefundStep = 'select-path';
  activePath: RefundPath = 'qr';

  isSubmitting = false;
  submissionError: string | null = null;
  generatedRequestId: string | null = null;
  liveRefundDoc: any = null;
  refundStreamSub?: Subscription;
  isCopiedCoupon = false;

  // Camera QR Scanner State
  isScanning = false;
  cameraError: string | null = null;
  showManualQrInputs = false;
  private mediaStream: MediaStream | null = null;
  private animationFrameId: number | null = null;

  // Form Fields - Strip QR Path
  deviceId = '';
  sessionId = '';
  sessionDate = '';

  // Form Fields - No QR Path
  boothLocation = '';
  approximateDate = '';
  approximateTime = '';
  paymentMethod = 'Card';
  paidAmount: number | null = null;

  // Active stores with machines mapped
  activeStores: any[] = [];
  selectedStoreDocId = '';
  selectedStoreFriendlyName = '';
  isLoadingStores = false;

  // Reason Selection
  selectedReasonCategory = '';
  reasonText = '';
  showCashRefundOption = false; // Hidden for now per business requirements
  wantsCashRefund = false;
  reasons: string[] = [
    'Booth charged me but did not start',
    'Booth stopped during the session',
    'Photos did not print',
    'Photos missing from online portal',
    'Printer produced an unusable result',
    'Camera or booth malfunctioned',
    'I was charged more than expected',
    'Other'
  ];

  // Attached Photo Evidence State (KAN-24)
  selectedEvidenceFiles: { file: File; previewUrl: string; sizeFormatted: string }[] = [];
  evidenceError: string | null = null;
  readonly MAX_ATTACHMENTS = 3;
  readonly MAX_SINGLE_FILE_SIZE = 5 * 1024 * 1024; // 5 MiB
  readonly MAX_AGGREGATE_SIZE = 15 * 1024 * 1024; // 15 MiB

  get userEmail(): string {
    return this.auth.currentUser?.email || localStorage.getItem('userEmail') || '';
  }

  get userUid(): string {
    return this.auth.currentUser?.uid || '';
  }

  ngOnInit(): void {
    this.isLoadingStores = true;
    this.portalService.getActiveStoresWithMachines().subscribe({
      next: (stores) => {
        this.activeStores = stores;
        this.isLoadingStores = false;
        if (this.boothLocation && !this.selectedStoreDocId) {
          const match = this.activeStores.find(s => s.storeId === this.boothLocation || s.id === this.boothLocation);
          if (match) {
            this.selectedStoreDocId = match.id;
            this.onStoreSelected();
          }
        }
      },
      error: (err) => {
        console.warn('Failed to load stores with machines:', err);
        this.isLoadingStores = false;
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['machine']) {
        this.deviceId = params['machine'];
        this.boothLocation = params['machine'];
      }
      if (params['date']) {
        this.sessionDate = params['date'];
        this.approximateDate = params['date'];
      }
      if (params['session']) {
        this.sessionId = params['session'];
        this.activePath = 'qr';
        if (!this.sessionDate) {
          const extracted = this.extractDateFromSession(this.sessionId);
          if (extracted) {
            this.sessionDate = extracted;
            this.approximateDate = extracted;
          } else if (this.deviceId) {
            this.lookupSessionDateFromFirestore(this.deviceId, this.sessionId);
          }
        }
        this.currentStep = 'select-reason';
      }
    });

    // Default for manual entry only if not set
    if (!this.approximateDate) {
      this.approximateDate = new Date().toISOString().split('T')[0];
    }
  }

  ngOnDestroy(): void {
    this.stopCameraScanner();
    if (this.refundStreamSub) {
      this.refundStreamSub.unsubscribe();
    }
    this.clearEvidencePreviews();
  }

  // --- Photo Evidence Attachments (KAN-24) ---

  onEvidenceFilesSelected(event: Event): void {
    this.evidenceError = null;
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    if (this.selectedEvidenceFiles.length + files.length > this.MAX_ATTACHMENTS) {
      this.evidenceError = `You can upload a maximum of ${this.MAX_ATTACHMENTS} files per refund request.`;
      input.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const allowedExts = ['.jpg', '.jpeg', '.png', '.webp'];

    let currentTotalSize = this.selectedEvidenceFiles.reduce((sum, item) => sum + item.file.size, 0);

    for (const file of files) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
        this.evidenceError = `File "${file.name}" is not supported. Please select JPEG, PNG, or WebP images only.`;
        input.value = '';
        return;
      }

      if (file.size > this.MAX_SINGLE_FILE_SIZE) {
        this.evidenceError = `File "${file.name}" exceeds the 5 MiB limit (${this.formatFileSize(file.size)}).`;
        input.value = '';
        return;
      }

      currentTotalSize += file.size;
      if (currentTotalSize > this.MAX_AGGREGATE_SIZE) {
        this.evidenceError = `Total upload size exceeds the 15 MiB limit. Please select smaller files.`;
        input.value = '';
        return;
      }
    }


    for (const file of files) {
      const previewUrl = URL.createObjectURL(file);
      this.selectedEvidenceFiles.push({
        file,
        previewUrl,
        sizeFormatted: this.formatFileSize(file.size)
      });
    }

    input.value = '';
  }

  removeEvidenceFile(index: number): void {
    this.evidenceError = null;
    if (index >= 0 && index < this.selectedEvidenceFiles.length) {
      const item = this.selectedEvidenceFiles[index];
      URL.revokeObjectURL(item.previewUrl);
      this.selectedEvidenceFiles.splice(index, 1);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private clearEvidencePreviews(): void {
    this.selectedEvidenceFiles.forEach(item => URL.revokeObjectURL(item.previewUrl));
    this.selectedEvidenceFiles = [];
    this.evidenceError = null;
  }

  // --- Step Navigation ---

  goToSelectPath(): void {
    this.stopCameraScanner();
    this.currentStep = 'select-path';
    this.submissionError = null;
  }

  goToScanQr(): void {
    this.activePath = 'qr';
    this.currentStep = 'scan-qr';
    this.submissionError = null;
    this.cameraError = null;
    this.showManualQrInputs = false;
    setTimeout(() => this.startCameraScanner(), 80);
  }

  goToManualInfo(): void {
    this.stopCameraScanner();
    this.activePath = 'no-qr';
    this.currentStep = 'manual-info';
    this.submissionError = null;
  }

  goBack(): void {
    this.submissionError = null;
    if (this.currentStep === 'scan-qr' || this.currentStep === 'manual-info') {
      this.goToSelectPath();
    } else if (this.currentStep === 'select-reason') {
      if (this.activePath === 'qr') {
        this.goToScanQr();
      } else {
        this.goToManualInfo();
      }
    }
  }

  onStoreSelected(): void {
    const store = this.activeStores.find(s => s.id === this.selectedStoreDocId);
    if (store) {
      this.selectedStoreFriendlyName = store.friendlyName || store.friendlyStoreName || store.name || store.storeId;
      this.boothLocation = this.selectedStoreFriendlyName;
      this.deviceId = store.storeId || store.id;
    }
  }

  continueFromManualInfo(): void {
    this.submissionError = null;
    if (!this.selectedStoreDocId && !this.boothLocation.trim()) {
      this.submissionError = 'Please select your store location from the dropdown.';
      return;
    }
    if (!this.approximateDate) {
      this.submissionError = 'Please select the date of your session.';
      return;
    }
    if (!this.paidAmount || this.paidAmount <= 0) {
      this.submissionError = 'Please enter a valid amount paid (e.g. 10.00).';
      return;
    }
    this.currentStep = 'select-reason';
  }

  continueFromManualQrInput(): void {
    this.submissionError = null;
    if (!this.sessionId.trim()) {
      this.submissionError = 'Please enter the Session ID.';
      return;
    }
    if (!this.deviceId.trim()) {
      this.submissionError = 'Please enter the Booth / Machine ID.';
      return;
    }
    this.currentStep = 'select-reason';
  }

  // --- Camera QR Scanner ---

  async startCameraScanner(): Promise<void> {
    this.stopCameraScanner();
    this.isScanning = true;
    this.cameraError = null;
    this.showManualQrInputs = false;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.cameraError = 'Camera access is not supported by your browser. Please enter details manually.';
      this.isScanning = false;
      this.showManualQrInputs = true;
      return;
    }

    try {
      let stream: MediaStream;
      try {
        // Try high-resolution back/environment camera first
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920, min: 640 },
            height: { ideal: 1080, min: 480 }
          }
        });
      } catch {
        // Fallback for laptop/desktop webcams without facingMode or resolution limits
        stream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      this.mediaStream = stream;

      if (this.scannerVideo && this.scannerVideo.nativeElement) {
        const video = this.scannerVideo.nativeElement;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        await video.play();
        this.requestScanFrame();
      }
    } catch (err: any) {
      console.warn('Camera access error:', err);
      this.isScanning = false;
      this.cameraError = 'Camera permission was denied or camera is unavailable. You can enter details manually.';
      this.showManualQrInputs = true;
    }
  }

  stopCameraScanner(): void {
    this.isScanning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  private requestScanFrame(): void {
    if (!this.isScanning) return;
    this.animationFrameId = requestAnimationFrame(() => this.scanFrame());
  }

  private async scanFrame(): Promise<void> {
    if (!this.isScanning || !this.scannerVideo || !this.scannerVideo.nativeElement) {
      return;
    }

    const video = this.scannerVideo.nativeElement;
    if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
      // 1. Fast path: Native browser BarcodeDetector API (hardware accelerated, highly forgiving on webcams)
      if ('BarcodeDetector' in window) {
        try {
          const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
          const barcodes = await barcodeDetector.detect(video);
          if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
            this.handleDecodedQr(barcodes[0].rawValue);
            return;
          }
        } catch {
          // Fallback gracefully to canvas jsQR if BarcodeDetector is unsupported or errors
        }
      }

      // 2. jsQR Canvas fallback with inversion & multi-exposure support
      const canvas = this.scannerCanvas?.nativeElement || document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Pass 1: Attempt standard and inverted scan (attemptBoth handles webcam contrast and glare)
        let code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'attemptBoth'
        });

        if (code && code.data) {
          this.handleDecodedQr(code.data);
          return;
        }

        // Pass 2: Center crop zoom (helpful for fixed-focus PC webcams held a few inches away)
        const cropSize = Math.min(canvas.width, canvas.height) * 0.7;
        const cropX = (canvas.width - cropSize) / 2;
        const cropY = (canvas.height - cropSize) / 2;
        const croppedData = ctx.getImageData(cropX, cropY, cropSize, cropSize);
        code = jsQR(croppedData.data, croppedData.width, croppedData.height, {
          inversionAttempts: 'attemptBoth'
        });

        if (code && code.data) {
          this.handleDecodedQr(code.data);
          return;
        }
      }
    }

    this.requestScanFrame();
  }

  extractDateFromSession(sessionId: string): string | null {
    if (!sessionId) return null;

    // Pattern 1: YYYY-MM-DD or YYYY_MM_DD (e.g. 2026-09-18_143022_SJC01)
    const matchHyphen = sessionId.match(/(20\d{2})[-_](\d{2})[-_](\d{2})/);
    if (matchHyphen) {
      return `${matchHyphen[1]}-${matchHyphen[2]}-${matchHyphen[3]}`;
    }

    // Pattern 2: YYYYMMDD (8 consecutive digits starting with 20)
    const matchCompact = sessionId.match(/(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/);
    if (matchCompact) {
      return `${matchCompact[1]}-${matchCompact[2]}-${matchCompact[3]}`;
    }

    // Pattern 3: Unix Milliseconds Timestamp (13 digits starting with 16, 17, 18, 19, 20)
    const matchMs = sessionId.match(/\b(1[6-9]\d{11}|20\d{11})\b/);
    if (matchMs) {
      try {
        const d = new Date(Number(matchMs[1]));
        if (!isNaN(d.getTime())) {
          return d.toISOString().split('T')[0];
        }
      } catch { }
    }

    return null;
  }

  getFriendlyDate(dateStr?: string | null): string {
    if (!dateStr) return 'Recent Photo Session';
    try {
      const trimmed = dateStr.trim();
      // Handle YYYY-MM-DD or YYYY/MM/DD
      const parts = trimmed.split(/[-/]/);
      if (parts.length === 3 && parts[0].length === 4) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }).format(d);
        }
      }
      // Handle compact YYYYMMDD
      if (/^\d{8}$/.test(trimmed)) {
        const year = parseInt(trimmed.substring(0, 4), 10);
        const month = parseInt(trimmed.substring(4, 6), 10) - 1;
        const day = parseInt(trimmed.substring(6, 8), 10);
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }).format(d);
        }
      }
      const d = new Date(trimmed);
      if (!isNaN(d.getTime())) {
        return new Intl.DateTimeFormat('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        }).format(d);
      }
    } catch { }
    return dateStr;
  }

  async lookupSessionDateFromFirestore(deviceId: string, sessionId: string, dateHint?: string | null): Promise<void> {
    try {
      const snap = await this.portalService.fetchSessionMetadata(deviceId, sessionId, dateHint);
      if (snap) {
        const dateVal = snap.sessionDate || snap.date || snap.createdAt || snap.timestamp || snap.StartTime;
        if (dateVal) {
          let resolvedDateStr = '';
          if (typeof dateVal === 'string') {
            resolvedDateStr = dateVal.includes('T') ? dateVal.split('T')[0] : dateVal;
          } else if (dateVal.toDate) {
            resolvedDateStr = dateVal.toDate().toISOString().split('T')[0];
          } else if (dateVal instanceof Date) {
            resolvedDateStr = dateVal.toISOString().split('T')[0];
          }
          if (resolvedDateStr) {
            this.sessionDate = resolvedDateStr;
            this.approximateDate = resolvedDateStr;
          }
        }
      }
    } catch (err) {
      console.warn('Could not lookup session date from Firestore:', err);
    }
  }

  handleDecodedQr(rawQrData: string): void {
    try {
      let parsedUrl: URL | null = null;
      try {
        parsedUrl = new URL(rawQrData);
      } catch {
        if (rawQrData.includes('?') || rawQrData.includes('/')) {
          try {
            parsedUrl = new URL(rawQrData, 'https://my.kphotolavie.com');
          } catch { }
        }
      }

      let extractedDate: string | null = null;

      if (parsedUrl) {
        const machine = parsedUrl.searchParams.get('machine') || parsedUrl.searchParams.get('deviceId') || parsedUrl.searchParams.get('m');
        const session = parsedUrl.searchParams.get('session') || parsedUrl.searchParams.get('sessionId') || parsedUrl.searchParams.get('s');
        const dateParam = parsedUrl.searchParams.get('date') || parsedUrl.searchParams.get('sessionDate') || parsedUrl.searchParams.get('d') || parsedUrl.searchParams.get('dt');

        const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
        const sessionIdx = pathSegments.indexOf('session');
        if (sessionIdx !== -1 && pathSegments[sessionIdx + 1]) {
          this.sessionId = pathSegments[sessionIdx + 1];
        } else if (session) {
          this.sessionId = session;
        }

        if (machine) this.deviceId = machine;
        if (dateParam) {
          extractedDate = dateParam;
        }
      } else {
        this.sessionId = rawQrData.trim();
      }

      // If no explicit date parameter in the QR URL, extract the picture date from sessionId
      if (!extractedDate && this.sessionId) {
        extractedDate = this.extractDateFromSession(this.sessionId);
      }

      if (extractedDate) {
        this.sessionDate = extractedDate;
        this.approximateDate = extractedDate;
      }

      if (this.sessionId) {
        this.stopCameraScanner();
        this.submissionError = null;

        // Asynchronously check Firestore session doc for exact picture capture date if not already found
        if (!this.sessionDate && this.deviceId) {
          this.lookupSessionDateFromFirestore(this.deviceId, this.sessionId, extractedDate);
        }

        if (typeof navigator.vibrate === 'function') {
          navigator.vibrate(100);
        }

        // Advance to next page (problem/reason selection) automatically
        this.currentStep = 'select-reason';
        return;
      }
    } catch (e) {
      console.error('Error parsing QR data:', e);
    }

    this.requestScanFrame();
  }

  toggleManualInputs(): void {
    this.showManualQrInputs = !this.showManualQrInputs;
    if (this.showManualQrInputs) {
      this.stopCameraScanner();
    } else {
      this.startCameraScanner();
    }
  }

  // --- Reason Selection & Submission ---

  selectReason(reason: string): void {
    this.selectedReasonCategory = reason;
    this.submissionError = null;
  }

  async submitRefund(): Promise<void> {
    this.submissionError = null;

    if (!this.userUid || !this.userEmail) {
      this.submissionError = 'You must be signed in with Google to submit a refund request.';
      return;
    }

    if (!this.selectedReasonCategory) {
      this.submissionError = 'Please select the issue that occurred during your session.';
      return;
    }

    if (!this.reasonText.trim()) {
      this.submissionError = 'Please provide details about what went wrong in the Incident Details box.';
      return;
    }

    if (this.reasonText.length > 200) {
      this.reasonText = this.reasonText.substring(0, 200);
    }

    this.isSubmitting = true;

    try {
      const isQr = this.activePath === 'qr';
      // For QR scans, prioritize the picture taken date from QR / session metadata over the scan date
      const resolvedDate = isQr
        ? (this.sessionDate || this.extractDateFromSession(this.sessionId) || null)
        : this.approximateDate;

      const resolvedStoreLocation = isQr 
        ? this.deviceId 
        : (this.selectedStoreFriendlyName || this.boothLocation);
      const resolvedDeviceId = isQr 
        ? this.deviceId 
        : (this.deviceId || this.selectedStoreFriendlyName || this.boothLocation);

      const filesToUpload = this.selectedEvidenceFiles.map(item => item.file);
      const requestId = await this.portalService.submitRefundRequestWithEvidence({
        requestorUid: this.userUid,
        requestorEmail: this.userEmail,
        deviceId: resolvedDeviceId,
        sessionId: isQr ? this.sessionId : null,
        sessionDate: resolvedDate,
        approximateTime: isQr ? null : this.approximateTime,
        storeLocation: resolvedStoreLocation,
        identificationMethod: isQr ? 'qr_scan' : 'guided_match',
        reasonCategory: this.selectedReasonCategory,
        reasonText: this.reasonText,
        originalPaymentMethod: this.paymentMethod,
        originalPaidAmount: isQr ? (this.paidAmount || 10.0) : (this.paidAmount || 0),
        wantsCashRefund: this.wantsCashRefund
      }, filesToUpload);

      this.generatedRequestId = requestId;
      this.currentStep = 'confirmed';
      this.subscribeToRefundUpdates(requestId);
    } catch (err: any) {
      console.error('Error creating refund request:', err);
      this.submissionError = err?.message || 'Failed to submit refund request. Please check your connection and try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  subscribeToRefundUpdates(requestId: string): void {
    if (this.refundStreamSub) {
      this.refundStreamSub.unsubscribe();
    }
    this.refundStreamSub = this.portalService.getRefundRequestStream(requestId).subscribe({
      next: (doc) => {
        if (doc) {
          this.liveRefundDoc = doc;
        }
      },
      error: (err) => {
        console.warn('Error streaming refund updates:', err);
      }
    });
  }

  formatCouponCode(code: string): string {
    if (!code || code.length !== 12) return code || '';
    return `${code.substring(0, 4)}-${code.substring(4, 8)}-${code.substring(8, 12)}`;
  }

  copyCouponCode(code: string): void {
    if (!code) return;
    const formatted = this.formatCouponCode(code);
    navigator.clipboard.writeText(formatted).then(() => {
      this.isCopiedCoupon = true;
      setTimeout(() => {
        this.isCopiedCoupon = false;
      }, 2500);
    }).catch(() => {
      this.isCopiedCoupon = true;
      setTimeout(() => {
        this.isCopiedCoupon = false;
      }, 2500);
    });
  }

  resetForm(): void {
    this.stopCameraScanner();
    if (this.refundStreamSub) {
      this.refundStreamSub.unsubscribe();
    }
    this.clearEvidencePreviews();
    this.currentStep = 'select-path';
    this.submissionError = null;
    this.generatedRequestId = null;
    this.liveRefundDoc = null;
    this.isCopiedCoupon = false;
    this.selectedReasonCategory = '';
    this.reasonText = '';
    this.wantsCashRefund = false;
    this.paidAmount = null;
    this.showManualQrInputs = false;
    this.sessionId = '';
    this.selectedStoreDocId = '';
    this.selectedStoreFriendlyName = '';
  }
}
